// attribute-rankings-section.tsx
//
// Competitive Intelligence, block 2: Attribute rankings.
//
// MOVED, not copied. This was the second half of competitive-intelligence-section
// on Brand Monitoring; it now mounts on the Deep Signal Competitive tab, where it
// replaced the Per-Model Competitive Presence card grid. Brand Monitoring keeps
// block 1 (Recommendation ranking) and no longer renders or imports any of this.
//
// Because it is a standalone section now, it resolves its own customer + run:
// fetchAttributes needs the customerId/run the ranking feed carries, so the feed
// is still read here, for those two ids only. Nothing of the ranking table is
// rendered from it.
//
// Two behaviours are locked by the design and must not drift:
//   1. Evidence: DRAWER for attribute evidence.
//   2. A brand with no score shows WHY it has none, never a zero or a dash.
import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  addCustomAttribute, attributeLabel, fetchAttributes, fetchMeasuredCompetitiveV2,
  formatAttributeScore, rankAttributeRows, STANDARD_ATTRIBUTE_ORDER,
  promoteAction,
  type AttributeBrandRow, type AttributeResult,
} from "@/app/services/brand/competitive-intelligence-api";
import { getModelLabel } from "@/app/constants/models";
import type { CreatedAction, GapDescriptor } from "@/app/services/fix-actions/create-action-from-gap";
import { SectionHead } from "./section-head";
import "./competitive-intelligence.css";

export type { CreatedAction, GapDescriptor };

interface Props {
  brandName?: string;
  /** Deep-links into Action Items for a matched or created action. */
  onNavigate?: (page: string, highlight?: string) => void;
  /** Creates a real Action Log row from a gap descriptor. See the note on
   *  the "what to do" block below for why this, and not the LLM generator. */
  onCreateAction?: (gap: GapDescriptor) => Promise<CreatedAction | null>;
  /** Reports the loaded attribute set up to the host. The Deep Signal
   *  Competitive tab builds its insight rail from these, so the scores are
   *  computed once here and read there rather than fetched twice. */
  onAttributesLoaded?: (payload: { attributes: AttributeResult[]; focal: string }) => void;
  /** Opens a specific attribute's tab from outside (the insight rail). Bump
   *  `selectionNonce` to re-apply the same attribute on a repeat click. */
  selectedAttribute?: string | null;
  selectionNonce?: number;
}

export function AttributeRankingsSection({
  brandName, onNavigate, onCreateAction,
  onAttributesLoaded, selectedAttribute, selectionNonce,
}: Props) {
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [runId, setRunId] = useState<number | null>(null);
  const [attrs, setAttrs] = useState<AttributeResult[]>([]);
  const [attrsPending, setAttrsPending] = useState(true);
  const [failed, setFailed] = useState(false);
  const [focalName, setFocalName] = useState<string>("");

  const [activeTab, setActiveTab] = useState<string>("value");
  const [infoOpen, setInfoOpen] = useState(false);
  const [drawer, setDrawer] = useState<{ attribute: string; brand: string } | null>(null);

  const [draft, setDraft] = useState("");
  const [pendingAttr, setPendingAttr] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [generating, setGenerating] = useState<string | null>(null);
  const [created, setCreated] = useState<Record<string, CreatedAction>>({});

  // The ranking feed is read for its customer + run ids only: the attribute
  // call is scoped to a specific measured run, and nothing else here knows one.
  useEffect(() => {
    const brand = (brandName || "").trim();
    if (!brand) return;
    let alive = true;
    setFailed(false);
    setAttrsPending(true);
    fetchMeasuredCompetitiveV2(brand)
      .then((res) => {
        if (!alive) return;
        setFocalName(res.focalName || "");
        setCustomerId(res.customerId ?? null);
        setRunId(res.run?.id ?? null);
        if (res.customerId && res.run?.id) {
          return fetchAttributes(res.customerId, res.run.id).then((a) => {
            if (alive) setAttrs(a.attributes || []);
          });
        }
        return undefined;
      })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setAttrsPending(false); });
    return () => { alive = false; };
  }, [brandName]);

  const ordered = useMemo(() => {
    const known = new Set(STANDARD_ATTRIBUTE_ORDER);
    const std = STANDARD_ATTRIBUTE_ORDER
      .map((s) => attrs.find((a) => a.attribute === s))
      .filter((a): a is AttributeResult => !!a);
    return [...std, ...attrs.filter((a) => !known.has(a.attribute))];
  }, [attrs]);

  const panel = ordered.find((a) => a.attribute === activeTab) ?? ordered[0];
  const isCustom = !!panel && !STANDARD_ATTRIBUTE_ORDER.includes(panel.attribute);

  const focal = focalName || brandName || "";

  // Report the scored set up to the host once it is loaded, so a sibling can
  // derive from the same numbers instead of fetching them again.
  useEffect(() => {
    if (!onAttributesLoaded || !ordered.length || !focal) return;
    onAttributesLoaded({ attributes: ordered, focal });
  }, [ordered, focal, onAttributesLoaded]);

  // External selection (a click on the insight rail). The nonce is in the deps
  // so re-clicking the card that is already open still re-applies and the host
  // can scroll to it.
  useEffect(() => {
    if (!selectedAttribute) return;
    setActiveTab(selectedAttribute);
    setInfoOpen(false);
  }, [selectedAttribute, selectionNonce]);

  // Ranked on the UNROUNDED score, ties resolved away from the client, and
  // brands inside the noise band sharing a rank.
  const panelRows = useMemo(
    () => (panel ? rankAttributeRows(panel.brands, focal) : []),
    [panel, focal],
  );

  const handleAdd = useCallback(async () => {
    const name = draft.trim();
    if (!name || !customerId || !runId) return;
    setPendingAttr(name);
    setAddError(null);
    try {
      const res = await addCustomAttribute(customerId, runId, name);
      setAttrs((prev) => [...prev.filter((a) => a.attribute !== res.attribute), res]);
      setActiveTab(res.attribute);
      setDraft("");
    } catch (e: any) {
      setAddError(String(e?.message || "Could not add that attribute."));
    } finally {
      setPendingAttr(null);
    }
  }, [draft, customerId, runId]);

  const gapFor = useCallback((attr: AttributeResult): GapDescriptor | null => {
    const rows = [...attr.brands].filter((b) => b.score != null)
      .sort((a, b) => (b.score! - a.score!));
    if (!rows.length) return null;
    const leader = rows[0];
    const me = attr.brands.find((b) => b.brand === focal);
    return {
      attribute: attr.attribute,
      definition: attr.definition,
      leaderBrand: leader.brand,
      clientScore: me?.score ?? null,
      leaderScore: leader.score,
      evidenceQuotes: (me?.quotes ?? []).slice(0, 3),
      defend: leader.brand === focal,
    };
  }, [focal]);

  const handleCreate = useCallback(async (attr: AttributeResult) => {
    const gap = gapFor(attr);
    if (!gap || !onCreateAction) return;
    setGenerating(attr.attribute);
    try {
      const rec = await onCreateAction(gap);
      if (rec) setCreated((p) => ({ ...p, [attr.attribute]: rec }));
    } finally {
      setGenerating(null);
    }
  }, [gapFor, onCreateAction]);

  // The drawer's "what to do" block has exactly two states, so opening it on
  // an attribute with no matched action starts writing one. Guarded so a
  // reopen cannot write a second row for the same attribute.
  useEffect(() => {
    if (!drawer || !onCreateAction) return;
    const attr = ordered.find((a) => a.attribute === drawer.attribute);
    if (!attr) return;
    const existing = created[attr.attribute]?.id ?? attr.generated_action_id ?? attr.action_id;
    if (existing || generating) return;
    void handleCreate(attr);
  }, [drawer, ordered, created, generating, onCreateAction, handleCreate]);

  if (failed) {
    return (
      <section className="ci">
        <SectionHead title="Attribute rankings" />
        <p className="ci-muted">Attribute rankings could not be loaded.</p>
      </section>
    );
  }

  // No measured run behind this brand: the attribute scores are computed from
  // stored responses, so without a run there is nothing to score against.
  if (!attrsPending && !ordered.length) {
    return (
      <section className="ci">
        <SectionHead title="Attribute rankings" />
        <p className="ci-muted">
          No attributes have been scored for this run yet.
        </p>
      </section>
    );
  }

  return (
    <section className="ci">
      <SectionHead title="Attribute rankings" />

      <div className="ci-tabs">
        {ordered.map((a) => (
          <button
            key={a.attribute}
            type="button"
            className={`ci-tab${activeTab === a.attribute ? " ci-tab--on" : ""}`}
            onClick={() => { setActiveTab(a.attribute); setInfoOpen(false); }}
          >
            {attributeLabel(a.attribute)}
          </button>
        ))}
        <button
          type="button"
          className={`ci-tab${activeTab === "__add" ? " ci-tab--on" : ""}`}
          onClick={() => setActiveTab("__add")}
        >
          + Add
        </button>
      </div>

      {activeTab === "__add" ? (
        <AddAttribute
          draft={draft} onDraft={setDraft} onAdd={handleAdd}
          pending={pendingAttr} error={addError}
          custom={ordered.filter((a) => !STANDARD_ATTRIBUTE_ORDER.includes(a.attribute))}
          onOpen={(slug) => setActiveTab(slug)}
        />
      ) : attrsPending && !panel ? (
        <div className="ci-panel">
          <div className="ci-skeletons">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="ci-sk" style={{ width: `${70 - i * 8}%` }} />)}
          </div>
          <div className="ci-mono-sm">Scoring against this account's stored responses</div>
        </div>
      ) : panel ? (
        <div className="ci-panel">
          <div className="ci-panel-head">
            <div className="ci-panel-title">
              <h4>{attributeLabel(panel.attribute)}</h4>
              {isCustom && <span className="ci-chip-violet">Custom</span>}
              <button type="button" className="ci-q" onClick={() => setInfoOpen((v) => !v)}>?</button>
            </div>
            <div className="ci-mono-sm">
              {panel.brands.filter((b) => !b.unmeasured).length} of {panel.brands.length} measured
            </div>
          </div>

          {/* Phase 13 item 3: for a CUSTOM attribute the normalised definition
              is always on screen, because the client cannot otherwise know what
              their made-up attribute name was actually measured as. Standard
              attributes keep the "?" toggle: their meaning is not in question. */}
          {isCustom
            ? <div className="ci-info-inline ci-info-inline--always">{panel.definition}</div>
            : infoOpen && <div className="ci-info-inline">{panel.definition}</div>}

          <div className="ci-rows">
            {panelRows.map(({ row: b, rank, sharedRank }) => (
              <button
                key={b.brand}
                type="button"
                className={`ci-row${b.brand === focal ? " ci-row--me" : ""}`}
                onClick={() => !b.unmeasured && setDrawer({ attribute: panel.attribute, brand: b.brand })}
              >
                {/* Brands inside the noise band share this number, and the
                    marker says so rather than implying a won position. */}
                <span className="ci-mono-rank">
                  {rank ?? ""}{sharedRank ? "=" : ""}
                </span>
                <span className="ci-row-name">{b.brand}</span>
                {b.score != null ? (
                  <span className="ci-track"><span className="ci-fill" style={{ width: `${b.score}%` }} /></span>
                ) : (
                  <span className="ci-chip-dashed">{unmeasuredLabel(b)}</span>
                )}
                <span className="ci-row-score">{formatAttributeScore(b, focal) ?? ""}</span>
                <span className="ci-row-chev">›</span>
              </button>
            ))}
          </div>

          <WhatToDo
            gap={gapFor(panel)}
            generating={generating === panel.attribute}
            createdId={created[panel.attribute]?.id ?? panel.action_id ?? panel.generated_action_id}
            canCreate={!!onCreateAction}
            onCreate={() => handleCreate(panel)}
            onOpen={(id) => onNavigate?.("fix-actions", id)}
          />
        </div>
      ) : null}

      {drawer && (() => {
        const attr = ordered.find((a) => a.attribute === drawer.attribute);
        // Match first, exactly as the attribute panel does: an action already
        // linked to this attribute is used rather than a second one written.
        const record = attr ? created[attr.attribute] : undefined;
        const actionId = record?.id ?? attr?.generated_action_id ?? attr?.action_id ?? null;
        return (
          <EvidenceDrawer
            attribute={drawer.attribute}
            brand={drawer.brand}
            definition={attr?.definition}
            isCustom={!!attr && !STANDARD_ATTRIBUTE_ORDER.includes(attr.attribute)}
            row={attr?.brands.find((b) => b.brand === drawer.brand)}
            gap={attr ? gapFor(attr) : null}
            generating={generating === drawer.attribute}
            createdId={actionId}
            record={record}
            onOpenAction={(id) => {
              setDrawer(null);
              // Following the link is the user surfacing this row, so promote
              // it out of the held-back state before navigating to it.
              void promoteAction(id).finally(() => onNavigate?.("fix-actions", id));
            }}
            onClose={() => setDrawer(null)}
          />
        );
      })()}
    </section>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────

/**
 * What to do about it.
 *
 * Match-first comes from the data layer (action_id). For an unmatched gap the
 * button writes a REAL row into the Action Log from the gap descriptor via the
 * existing ingest path, and shows the designed generating state while it does.
 *
 * Deviation worth knowing: the copy on that action is composed deterministically
 * from the measurement (attribute, leader, both scores), NOT written by the LLM
 * generator. See create-action-from-gap.ts.
 */
function WhatToDo({ gap, generating, createdId, canCreate, onCreate, onOpen }: {
  gap: GapDescriptor | null; generating: boolean;
  createdId: string | null | undefined; canCreate: boolean;
  onCreate: () => void; onOpen: (id: string) => void;
}) {
  if (!gap) return null;
  return (
    <div className="ci-wtd">
      <div className="ci-sub">What to do about it</div>
      {generating ? (
        <div className="ci-gen">
          <div className="ci-gen-bar" /><div className="ci-gen-bar ci-gen-bar--short" />
          <div className="ci-mono-sm">Writing an action for this gap</div>
        </div>
      ) : createdId ? (
        <>
          <div className="ci-move">{moveLine(gap)}</div>
          <button type="button" className="ci-link" onClick={() => onOpen(createdId)}>
            Open in Action Items ↗
          </button>
        </>
      ) : canCreate ? (
        <>
          <div className="ci-move">{moveLine(gap)}</div>
          <button type="button" className="ci-btn ci-btn--sm" onClick={onCreate}>
            Create action
          </button>
        </>
      ) : (
        <div className="ci-muted">
          No action is linked to this attribute yet. Action creation is not available from
          this context.
        </div>
      )}
    </div>
  );
}

function moveLine(gap: GapDescriptor): string {
  const label = attributeLabel(gap.attribute);
  if (gap.defend) {
    return `Defend the lead on ${label.toLowerCase()}: keep the evidence models quote current.`;
  }
  const delta = gap.leaderScore != null && gap.clientScore != null
    ? ` (${gap.leaderScore} vs ${gap.clientScore})`
    : "";
  return `Close the ${label.toLowerCase()} gap against ${gap.leaderBrand}${delta}.`;
}

function AddAttribute({ draft, onDraft, onAdd, pending, error, custom, onOpen }: {
  draft: string; onDraft: (v: string) => void; onAdd: () => void;
  pending: string | null; error: string | null;
  custom: AttributeResult[]; onOpen: (slug: string) => void;
}) {
  return (
    <div className="ci-panel">
      <h4 className="ci-add-title">Add an attribute</h4>
      <div className="ci-info-inline">
        A custom attribute is scored against the responses already stored for this account.
        No new measurement run is triggered, so the result covers the same models and the
        same reads as the standard six.
      </div>

      <div className="ci-add-form">
        <label className="ci-label" htmlFor="ci-attr">Attribute</label>
        <input
          id="ci-attr"
          className="ci-input"
          type="text"
          placeholder="A word or phrase, for example durability"
          value={draft}
          disabled={!!pending}
          onChange={(e) => onDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onAdd(); }}
        />
        <button type="button" className="ci-btn" disabled={!draft.trim() || !!pending} onClick={onAdd}>
          {pending ? "Scoring…" : "Add attribute"}
        </button>
        {error && <div className="ci-error">{error}</div>}
      </div>

      {pending && (
        <div className="ci-pending">
          <div className="ci-pending-name">{pending}</div>
          {[0, 1, 2, 3].map((i) => <div key={i} className="ci-sk" style={{ width: `${80 - i * 12}%` }} />)}
          <div className="ci-mono-sm">Scoring against this account's stored responses</div>
        </div>
      )}

      {custom.length > 0 && (
        <div className="ci-custom-list">
          <div className="ci-sub">Added by this account</div>
          {custom.map((c) => (
            <button key={c.attribute} type="button" className="ci-custom-row" onClick={() => onOpen(c.attribute)}>
              <span>{attributeLabel(c.attribute)}</span><span className="ci-muted">Open ›</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The evidence drawer (phase 12e), to the CD drawer spec.
 *
 * The "what to do" block is ALWAYS present in one of exactly two states,
 * ready or generating. There is no empty state and no fallback copy.
 */
function EvidenceDrawer({ attribute, brand, definition, isCustom, row, gap, generating, createdId, record, onOpenAction, onClose }: {
  attribute: string; brand: string;
  definition?: string; isCustom?: boolean;
  row?: AttributeBrandRow;
  gap: GapDescriptor | null;
  generating: boolean;
  createdId: string | null | undefined;
  record?: CreatedAction;
  onOpenAction: (id: string) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    // CAPTURE phase, and the event is consumed. The drawer is the topmost
    // surface, so Escape must close IT first; without stopping propagation the
    // assistant panel behind it also sees the key and both close at once.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    // Body scroll is locked while the drawer is open, then restored to
    // whatever it was rather than assumed to have been "".
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // PORTALLED to <body>. The section renders deep inside the page, and an
  // ancestor there establishes its own stacking context, so the drawer's
  // z-index was being resolved against that ancestor rather than the document:
  // the assistant panel (fixed, z-50) painted straight over a drawer nominally
  // at z-60. A portal puts the drawer where its z-index means what it says.
  return createPortal(
    <div className="ci-drawer-scrim" onClick={onClose} role="presentation">
      <aside className="ci-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`${brand} evidence`}>
        <div className="ci-drawer-head">
          <div className="ci-drawer-head-col">
            <div className="ci-eyebrow">{attributeLabel(attribute)}</div>
            <div className="ci-drawer-brand">{brand}</div>
            <div className="ci-drawer-meta">{metaLine(row)}</div>
          </div>
          <button type="button" className="ci-x" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="ci-drawer-body">
          {/* The custom attribute's definition travels with it into the
              drawer, so the evidence is read against what was measured. */}
          {isCustom && definition && (
            <div className="ci-info-inline ci-info-inline--always">{definition}</div>
          )}
          <div className="ci-sub">Evidence</div>
          {row?.quotes?.length ? (
            <div className="ci-drawer-quotes">
              {row.quotes.map((q, i) => (
                <div key={i} className="ci-dq">
                  <div className="ci-dq-text">{q.text}</div>
                  {/* Attribution is the model name only, per the design. */}
                  <div className="ci-dq-model">{getModelLabel(q.model) || q.model}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="ci-muted">No quotes were kept for this brand on this attribute.</div>
          )}

          <div className="ci-drawer-rule" />

          <div className="ci-sub ci-sub--tight">What to do about it</div>
          {generating || !createdId ? (
            <div className="ci-gen">
              <div className="ci-gen-bar" /><div className="ci-gen-bar ci-gen-bar--short" />
              <div className="ci-mono-sm">Writing an action for this gap</div>
            </div>
          ) : (
            <>
              <div className="ci-move">{gap ? moveLine(gap) : ""}</div>
              <div className="ci-chips">
                {/* Impact: the record's own value wins; the measured-gap
                    derivation is only a fallback when the record has none. */}
                <span className={`ci-chip-impact${chipIsHigh(record, gap) ? " ci-chip-impact--high" : ""}`}>
                  {impactChipLabel(record, gap)}
                </span>
                {/* Effort: rendered ONLY if the record carries one, verbatim.
                    Never derived, defaulted, or invented, so it is normally
                    absent and the chip simply does not appear. */}
                {record?.effort ? (
                  <span className="ci-chip-effort">{record.effort}</span>
                ) : null}
              </div>
              <button type="button" className="ci-cta" onClick={() => onOpenAction(createdId)}>
                Open in Action Items ↗
              </button>
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

/**
 * "Score {score} of 100 · {models} models · {span_count} spans scored".
 *
 * Every value is real and any unavailable segment is DROPPED, never invented
 * or defaulted to zero. The label says what the number counts: these are
 * scored spans, not whole responses, and calling them responses would
 * overstate the corpus.
 */
function metaLine(row?: AttributeBrandRow): string {
  const parts: string[] = [];
  if (row?.score != null) parts.push(`Score ${row.score} of 100`);
  if (row?.model_count) parts.push(`${row.model_count} model${row.model_count === 1 ? "" : "s"}`);
  if (row?.span_count) parts.push(`${row.span_count} span${row.span_count === 1 ? "" : "s"} scored`);
  return parts.join(" · ") || unmeasuredLabel(row);
}

/**
 * Why a brand has no score (phase 13). "signal" means spans about the brand
 * exist but none of them address this attribute, which is a different fact
 * from never having been looked at, and neither is a number.
 */
function unmeasuredLabel(row?: { unmeasured_reason?: "signal" | "no_spans" | null }): string {
  return row?.unmeasured_reason === "signal"
    ? "Not enough signal on this attribute"
    : "Not yet measured";
}

/**
 * Impact is read off the MEASURED gap, not asserted. A gap of 20 points or
 * more on a 0-100 scale is the high band. Defending a lead is not high impact.
 * There is no effort chip: nothing in the payload measures effort, and a
 * plausible-looking "Medium effort" would be invented. See the report.
 */
function impactIsHigh(gap: GapDescriptor | null): boolean {
  if (!gap || gap.defend) return false;
  if (gap.leaderScore == null || gap.clientScore == null) return false;
  return gap.leaderScore - gap.clientScore >= 20;
}

function impactLabel(gap: GapDescriptor | null): string {
  return impactIsHigh(gap) ? "High impact" : "Medium impact";
}

/** The record's impact if it has one, else the measured-gap derivation. */
function impactChipLabel(record: CreatedAction | undefined, gap: GapDescriptor | null): string {
  const fromRecord = (record?.impactLevel || "").trim();
  return fromRecord ? `${fromRecord} impact` : impactLabel(gap);
}

function chipIsHigh(record: CreatedAction | undefined, gap: GapDescriptor | null): boolean {
  const fromRecord = (record?.impactLevel || "").trim();
  if (fromRecord) return /^high/i.test(fromRecord);
  return impactIsHigh(gap);
}
